'use client'

import { type ComponentProps, createContext, useContext, useEffect, useRef, useState } from 'react'

import { useControllableState } from '@radix-ui/react-use-controllable-state'
import { ChevronsUpDownIcon } from 'lucide-react'

import { cn } from '../utils'
import { Button } from './Button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './Command'
import { Popover, PopoverContent, PopoverTrigger } from './Popover'

type TComboboxData = {
  label: string
  value: string
}

type TComboboxContextType = {
  data: TComboboxData[]
  onOpenChange: (open: boolean) => void
  onValueChange: (value: string) => void
  open: boolean | undefined
  setWidth: (width: number) => void
  type: string
  value: string | undefined
  width: number
}

const ComboboxContext = createContext<TComboboxContextType>({
  data: [],
  onOpenChange: () => {},
  onValueChange: () => {},
  open: false,
  setWidth: () => {},
  type: 'item',
  value: '',
  width: 200,
})

export type TComboboxProps = {
  data: TComboboxData[]
  defaultValue?: string
  onOpenChange?: (open: boolean) => void
  onValueChange?: (value: string) => void
  open?: boolean
  type: string
  value?: string
} & ComponentProps<typeof Popover>

export const Combobox = ({
  data,
  defaultOpen = false,
  defaultValue,
  onOpenChange: controlledOnOpenChange,
  onValueChange: controlledOnValueChange,
  open: controlledOpen,
  type,
  value: controlledValue,
  ...props
}: TComboboxProps) => {
  const [value, onValueChange] = useControllableState({
    defaultProp: defaultValue ?? '',
    onChange: controlledOnValueChange,
    prop: controlledValue,
  })
  const [open, onOpenChange] = useControllableState({
    defaultProp: defaultOpen,
    onChange: controlledOnOpenChange,
    prop: controlledOpen,
  })
  const [width, setWidth] = useState(200)

  return (
    <ComboboxContext.Provider
      value={{
        data,
        onOpenChange,
        onValueChange,
        open,
        setWidth,
        type,
        value,
        width,
      }}
    >
      <Popover {...props} onOpenChange={onOpenChange} open={open} />
    </ComboboxContext.Provider>
  )
}

export type TComboboxTriggerProps = ComponentProps<typeof Button>

export const ComboboxTrigger = ({ children, ...props }: TComboboxTriggerProps) => {
  const { data, setWidth, type, value } = useContext(ComboboxContext)
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // Create a ResizeObserver to detect width changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = (entry.target as HTMLElement).offsetWidth
        if (newWidth) {
          setWidth?.(newWidth)
        }
      }
    })

    if (ref.current) {
      resizeObserver.observe(ref.current)
    }

    // Clean up the observer when component unmounts
    return () => {
      resizeObserver.disconnect()
    }
  }, [setWidth])

  return (
    <PopoverTrigger asChild>
      <Button variant="outline" {...props} ref={ref}>
        {children ?? (
          <span className="flex w-full items-center justify-between gap-2 truncate">
            {value ? data.find((item) => item.value === value)?.label : `Select ${type}...`}
            <ChevronsUpDownIcon className="shrink-0 text-muted-foreground" size={16} />
          </span>
        )}
      </Button>
    </PopoverTrigger>
  )
}

export type TComboboxContentProps = ComponentProps<typeof Command>

export const ComboboxContent = ({ className, ...props }: TComboboxContentProps) => {
  const { width } = useContext(ComboboxContext)

  return (
    <PopoverContent className={cn('p-0', className)} style={{ width }}>
      <Command {...props} />
    </PopoverContent>
  )
}

export type TComboboxInputProps = ComponentProps<typeof CommandInput>

export const ComboboxInput = (props: TComboboxInputProps) => {
  const { type } = useContext(ComboboxContext)

  return <CommandInput placeholder={`Search ${type}...`} {...props} />
}

export type TComboboxListProps = ComponentProps<typeof CommandList>

export const ComboboxList = (props: TComboboxListProps) => <CommandList {...props} />

export type TComboboxEmptyProps = ComponentProps<typeof CommandEmpty>

export const ComboboxEmpty = ({ children, ...props }: TComboboxEmptyProps) => {
  const { type } = useContext(ComboboxContext)

  return (
    <CommandEmpty {...props}>
      {children ??
        (type == 'resume' ? (
          <span>
            {' '}
            No resume found. Create one on the{' '}
            <a className="underline" href="https://app.usecanyon.com/resumes">
              resumes page
            </a>
          </span>
        ) : (
          `No ${type} found.`
        ))}
    </CommandEmpty>
  )
}

export type TComboboxGroupProps = ComponentProps<typeof CommandGroup>

export const ComboboxGroup = (props: TComboboxGroupProps) => <CommandGroup {...props} />

export type TComboboxItemProps = ComponentProps<typeof CommandItem>

export const ComboboxItem = (props: TComboboxItemProps) => {
  const { onOpenChange, onValueChange, value } = useContext(ComboboxContext)

  return (
    <CommandItem
      onSelect={(currentValue) => {
        onValueChange(currentValue === value ? '' : currentValue)
        onOpenChange(false)
      }}
      {...props}
    />
  )
}

export type TComboboxSeparatorProps = ComponentProps<typeof CommandSeparator>

export const ComboboxSeparator = (props: TComboboxSeparatorProps) => <CommandSeparator {...props} />
